import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Divider, Stack, TextField, Autocomplete, Chip } from '@mui/material';
// components
import TextMaxLine from '../../../components/TextMaxLine';
import { FormProvider, RHFSwitch, RHFTextField } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { apiErrorText } from 'src/utils/apiErrorText';
import { Category } from 'src/@types/Category';
import { UploadAvatar } from 'src/components/upload';
import {
  CategoryFormValues,
  addKeyword,
  categoryFormValues,
  categoryPayload,
  categorySchema,
  firstErrorMessage,
  orderServerError,
  removeKeywordAt,
} from './categoryPayload';
// ----------------------------------------------------------------------

type Props = {
  isEdit: boolean;
  currentCategory?: Category;
  handleClose(): void;
};

type KeywordKey = 'keywords' | 'keywords_ar';

export default function CategoryNewEditForm({ isEdit, currentCategory, handleClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  // The form holds only its own fields (categoryPayload): never the list row,
  // whose other keys (active, slug, seo_*) would be sent back stale.
  const defaultValues = useMemo(() => categoryFormValues(currentCategory), [currentCategory]);

  const methods = useForm<CategoryFormValues>({
    resolver: yupResolver(categorySchema(isEdit)),
    defaultValues,
  });

  const {
    reset,
    watch,
    handleSubmit,
    setValue,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();

  useEffect(() => {
    reset(categoryFormValues(currentCategory));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentCategory]);

  const onSubmit = async (data: CategoryFormValues) => {
    const body = categoryPayload(data, isEdit, currentCategory);
    try {
      if (currentCategory)
        await axiosInstance.patch(APIS.CATEGORIES.PATCH(currentCategory._id as string), body);
      else await axiosInstance.post(APIS.CATEGORIES.ADD, body);
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err: any) {
      // The axios instance rejects with the response body (or a string): one
      // readable message, and an `order` 400 also under the field.
      const orderError = orderServerError(err);
      if (orderError) setError('order', { type: 'server', message: orderError });
      enqueueSnackbar(
        apiErrorText(err, !isEdit ? 'Unable to create Path' : 'Unable to edit Path'),
        { variant: 'error' }
      );
    }
  };

  const uploadImage = async (file: File, field: 'image' | 'icon') => {
    const form = new FormData();
    form.append('file', file);
    try {
      const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
      const url = (response?.data as any)?.url ?? (response?.data as any)?.data?.url;
      if (url) setValue(field, url as string, { shouldValidate: true });
    } catch (err) {
      enqueueSnackbar(apiErrorText(err, 'Upload failed'), { variant: 'error' });
    }
  };

  const keywordInput = (key: KeywordKey, label: string) => (
    <Autocomplete
      multiple
      id={key}
      options={[]}
      value={values[key] ?? []}
      freeSolo
      renderTags={(value: string[]) =>
        value.map((option: string, index: number) => (
          <Chip
            key={`${option}-${index}`}
            variant="outlined"
            label={option}
            onDelete={() =>
              setValue(key, removeKeywordAt(values[key], index), { shouldValidate: !!errors[key] })
            }
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          error={!!errors[key]}
          helperText={
            firstErrorMessage(errors[key]) ||
            'Press Enter after each keyword (3 characters or more)'
          }
          name={key}
          onKeyDown={(e) => {
            const input = e.target as HTMLInputElement;
            if (e.key === 'Enter') {
              e.preventDefault();
              setValue(key, addKeyword(values[key], input?.value), { shouldValidate: !!errors[key] });
            }
            // Backspace removes the last keyword only from an empty input (it
            // used to drop one on every key press while typing).
            if (e.key === 'Backspace' && !input?.value && (values[key] || []).length) {
              setValue(key, (values[key] || []).slice(0, -1));
            }
          }}
          variant="outlined"
          label={label}
          placeholder="Keywords"
          maxRows={3}
        />
      )}
    />
  );

  return (
    <Stack height={'100%'} style={{ overflow: 'scroll' }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'20px'} marginTop={'20px'} fontSize={'22px'}>
          {isEdit ? ' Edit Path' : ' Create Path'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'90%'}>
          <Stack direction={'column'} spacing={5}>
            <Stack direction={'row'} justifyContent={'center'} spacing={7}>
              <div>
                <UploadAvatar
                  accept={{ 'image/*': [] }}
                  error={errors.image ? true : false}
                  label="Path Image"
                  file={values.image}
                  onDropAccepted={(file) => uploadImage(file[0], 'image')}
                />
                <p style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '10px' }}>
                  {errors.image?.message}
                </p>
              </div>

              <Divider orientation="vertical" />
              <div>
                <UploadAvatar
                  accept={{ 'image/*': [] }}
                  label="Path Icon"
                  error={errors.icon ? true : false}
                  file={values.icon}
                  onDropAccepted={(file) => uploadImage(file[0], 'icon')}
                />
                <p style={{ color: '#FF4842', fontSize: '0.75rem', marginTop: '10px' }}>
                  {errors.icon?.message}
                </p>
              </div>
            </Stack>

            <RHFTextField fullWidth name="title" label="English Path Title" />
            <RHFTextField fullWidth name="title_ar" label="Arabic Path Title" />

            {/* categories-order-reset C1: the order the website lists paths in. */}
            <RHFTextField
              name="order"
              type="number"
              label="Display order"
              helperText={
                isEdit
                  ? 'Lower numbers are shown first on the website (0 to 1000).'
                  : 'Lower numbers are shown first on the website (0 to 1000). Empty = after the last path.'
              }
              inputProps={{ step: 'any', inputMode: 'numeric', 'data-testid': 'path-order' }}
            />
            <RHFSwitch
              name="showOnHome"
              label="Show in the home page learning-paths picker"
              data-testid="path-show-on-home"
            />

            <RHFTextField
              multiline
              minRows={2}
              name="description"
              maxRows={3}
              label="English Description"
            />
            <RHFTextField
              multiline
              minRows={2}
              name="description_ar"
              maxRows={3}
              label="Arabic Description"
            />

            {keywordInput('keywords', 'English Keywords')}
            {keywordInput('keywords_ar', 'Arabic Keywords')}
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Path' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
